import { CalendarCheck, ChevronDown, ChevronUp, ClipboardList, List, LogOut, Receipt } from "lucide-react";
import { Box, Button, Menu, useTheme } from "@mui/material";
import { MenuItem } from "@powerdiary/components-library/lib";
import { useState } from "react";
import { useAuthorizationContext, useClientPortalConfigContext } from "../../../../context";
import { useClientPortalNavigate } from "../../../../hooks/useClientPortalRouter";
import { useBookingAccess } from "../../../../hooks/useBookingAccess";
import { ROUTES } from "../../../../routes";
import LoadingOverlay from "../../../Shared/LoadingOverlay";
import { useClientPortalUserLogout } from "../useClientPortalUserLogout";
import { getBookGroupBookingLabel } from "../../../../utils/groupBookingLabel";

import styles from "./HeaderDesktop.module.css";

export default function HeaderDesktop() {
    const navigateTo = useClientPortalNavigate();
    const { userDetails, logoutUser } = useAuthorizationContext();
    const { clientPortalConfig } = useClientPortalConfigContext();
    const { logout, isLoading } = useClientPortalUserLogout();
    const { canBookAppointments, canBookClasses } = useBookingAccess();
    const theme = useTheme();
    const groupBookingLabel = getBookGroupBookingLabel(clientPortalConfig.groupBookingLabel);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigateToUrl = (url: string) => {
        navigateTo(url);
        handleClose();
    };

    const handleLogout = async () => {
        handleClose();
        await logout();
        await logoutUser();
    };

    const ChevronIcon = open ? ChevronUp : ChevronDown;

    return (
        <Box data-test-id="header_desktop">
            <Box className={styles.actions} data-test-id="header_desktop_actions">
                <Button
                    data-test-id="button_action_home"
                    variant="text"
                    onClick={() => navigateToUrl(ROUTES.HOME.urlPath)}
                >
                    Home
                </Button>

                {canBookAppointments && (
                    <Button
                        data-test-id="button_action_book_appointment"
                        variant="text"
                        onClick={() => navigateToUrl(ROUTES.APPOINTMENT_BOOKING.urlPath)}
                    >
                        Book Appointment
                    </Button>
                )}
                {canBookClasses && (
                    <Button
                        data-test-id="button_action_book_class"
                        variant="text"
                        onClick={() => navigateToUrl(ROUTES.CLASS_BOOKING.urlPath)}
                    >
                        {groupBookingLabel}
                    </Button>
                )}

                <Button
                    id="user_action_button"
                    data-test-id="button_user_action_menu"
                    variant="text"
                    onClick={handleClick}
                    endIcon={<ChevronIcon size={13} strokeWidth={2} aria-hidden />}
                >
                    <Box component="span" className={styles.userMenu}>
                        {userDetails.friendlyName}
                    </Box>
                </Button>

                <Menu
                    id="user_action_menu"
                    data-test-id="menu_user_actions"
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    slotProps={{
                        list: {
                            "aria-labelledby": "user_action_button",
                            dense: true,
                        },
                    }}
                >
                    {clientPortalConfig.featureAccess?.canViewUpcomingAppointments && (
                        <MenuItem
                            data-test-id="menu_item_appointments"
                            onClick={() => navigateToUrl(ROUTES.APPOINTMENTS.urlPath)}
                            className={styles.menuItem}
                        >
                            <CalendarCheck
                                data-test-id={`icon_header_list_item_appointment_booking`}
                                color={theme.palette.text.secondary}
                                size={20}
                                strokeWidth={2}
                                style={{ minWidth: "24px" }}
                                aria-hidden
                            />
                            Appointments
                        </MenuItem>
                    )}
                    {clientPortalConfig.featureAccess?.canViewInvoicesList && (
                        <MenuItem
                            data-test-id="menu_item_invoices"
                            onClick={() => navigateToUrl(ROUTES.INVOICES.urlPath)}
                            className={styles.menuItem}
                        >
                            <Receipt
                                data-test-id={`icon_header_list_item_appointment_booking`}
                                color={theme.palette.text.secondary}
                                size={20}
                                strokeWidth={2}
                                style={{ minWidth: "24px" }}
                                aria-hidden
                            />
                            Invoices
                        </MenuItem>
                    )}
                    {clientPortalConfig.featureAccess?.canAccessFormsPage && (
                        <MenuItem
                            data-test-id="menu_item_forms"
                            onClick={() => navigateToUrl(ROUTES.FORMS.urlPath)}
                            className={styles.menuItem}
                        >
                            <List
                                data-test-id={`icon_header_list_item_appointment_booking`}
                                color={theme.palette.text.secondary}
                                size={20}
                                strokeWidth={2}
                                style={{ minWidth: "24px" }}
                                aria-hidden
                            />
                            Forms
                        </MenuItem>
                    )}
                    {clientPortalConfig.featureAccess?.canRequestWaitlist && (
                        <MenuItem
                            data-test-id="menu_item_waitlist_requests"
                            onClick={() => navigateToUrl(ROUTES.WAITLIST_REQUEST.urlPath)}
                            className={styles.menuItem}
                        >
                            <ClipboardList
                                data-test-id={`icon_header_list_item_appointment_booking`}
                                color={theme.palette.text.secondary}
                                size={20}
                                strokeWidth={2}
                                style={{ minWidth: "24px" }}
                                aria-hidden
                            />
                            Waitlist Request
                        </MenuItem>
                    )}
                    <MenuItem
                        data-test-id="menu_item_logout"
                        onClick={() => void handleLogout()}
                        className={styles.menuItem}
                    >
                        <LogOut
                            data-test-id={`icon_header_list_item_appointment_booking`}
                            color={theme.palette.text.secondary}
                            size={20}
                            strokeWidth={2}
                            style={{ minWidth: "24px" }}
                            aria-hidden
                        />
                        Log out
                    </MenuItem>
                </Menu>
            </Box>

            {isLoading && <LoadingOverlay />}
        </Box>
    );
}
